import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  AddCircle,
  ArrowRight2,
  CloseCircle,
  DocumentText,
  Element3,
  Link1,
  PenAdd,
  Setting2,
  Tag2,
  TickCircle,
} from "iconsax-react";
import { Link } from "@tanstack/react-router";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ProductIconTile, SefanoCheckbox, SefanoIconButton, StatusBadge } from "@/components/Atoms";
import {
  FloatingTextField,
  QuantityStepper,
  SelectField,
  SefanoButton,
} from "@/components/Molecules";
import { PageTemplate } from "@/components/Templates/Page";
import { formatPersianNumber } from "@/lib/format/persian";
import {
  PRODUCT_ICON_OPTIONS,
  renderProductIcon,
} from "@/lib/products/productIcons";
import type { CreateProductTemplateProps } from "./CreateProduct.template.types";
import * as styles from "./CreateProduct.template.styles";

export type { CreateProductTemplateProps } from "./CreateProduct.template.types";

const DESKTOP_IMAGE_SIZE = 120;
const MOBILE_IMAGE_SIZE = 96;
const DESKTOP_ICON_TILE_SIZE = 46;
const MOBILE_ICON_TILE_SIZE = 40;

export function CreateProductTemplate({
  mode = "create",
  isLoadingProduct = false,
  isProductError = false,
  register,
  control,
  handleSubmit,
  errors,
  canSubmit,
  isPending,
  isSuccess,
  createdName,
  fileInputRef,
  imagePreview,
  iconId,
  showIconPicker,
  showSku,
  showDescription,
  categoryModalOpen,
  newCategoryName,
  categoryOptions,
  selectedCategoryName,
  previewStatus,
  watchedName,
  watchedPrice,
  watchedStock,
  watchedDescription,
  submitHint,
  priceFieldState,
  priceHelper,
  newCategoryHelper,
  cannotSaveCategory,
  isDuplicateCategory,
  handleImageSelect,
  handleImageChange,
  toggleIconPicker,
  handleSelectIcon,
  handleShowSku,
  handleShowDescription,
  handleOpenCategoryModal,
  handleCloseCategoryModal,
  handleCategoryChange,
  handleSaveNewCategory,
  setNewCategoryName,
  handleCreateAnother,
  onSubmit,
}: CreateProductTemplateProps) {
  const { t } = useTranslation(["products", "common"]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isEditMode = mode === "edit";

  const pageTitle = isEditMode ? t("products:edit.title") : t("products:create.title");
  const pageSubtitle = isEditMode ? t("products:edit.subtitle") : t("products:create.subtitle");
  const submitLabel = isEditMode ? t("products:edit.submit") : t("products:create.submit");
  const successTitle = isEditMode
    ? t("products:edit.successTitle")
    : t("products:create.successTitle");
  const successDescriptionDesktop = isEditMode
    ? t("products:edit.successDescription", { name: createdName })
    : t("products:create.successDescription", { name: createdName });
  const successDescriptionMobile = isEditMode
    ? t("products:edit.successDescriptionMobile", { name: createdName })
    : t("products:create.successDescriptionMobile", { name: createdName });

  if (isLoadingProduct) {
    return (
      <PageTemplate maxWidth={1120}>
        <Box sx={styles.loadingWrap}>
          <CircularProgress />
        </Box>
      </PageTemplate>
    );
  }

  if (isProductError) {
    return (
      <PageTemplate maxWidth={1120}>
        <Box sx={styles.notFoundWrap}>
          <Typography sx={styles.successTitle}>{t("products:edit.notFoundTitle")}</Typography>
          <Typography sx={styles.successDescription}>{t("products:edit.notFoundDescription")}</Typography>
          <Link to="/products" style={{ textDecoration: "none", marginTop: 16 }}>
            <SefanoButton>{t("products:create.successBack")}</SefanoButton>
          </Link>
        </Box>
      </PageTemplate>
    );
  }

  const previewName = watchedName.trim() || t("products:create.preview.defaultName");
  const previewPrice = watchedPrice > 0 ? formatPersianNumber(watchedPrice) : "۰";
  const previewIcon = renderProductIcon(
    iconId,
    21,
    theme.palette.primary.main,
  );

  const renderImageSlot = (size: number, radius: number) => (
    <Box sx={styles.imageRow}>
      {imagePreview ? (
        <Box
          component="img"
          src={imagePreview}
          alt=""
          sx={styles.imagePreview(size, radius)}
          onClick={handleImageSelect}
        />
      ) : (
        <Box sx={styles.imageSlot(size, radius)} onClick={handleImageSelect}>
          <Typography sx={styles.imagePlaceholderText}>
            {t("products:create.fields.image")}
          </Typography>
        </Box>
      )}
      <Box sx={styles.imageMeta}>
        <Typography sx={styles.imageTitle}>{t("products:create.fields.image")}</Typography>
        <Typography sx={styles.imageHint}>{t("products:create.imageHint")}</Typography>
        <Box
          component="button"
          type="button"
          sx={styles.ghostLink}
          onClick={toggleIconPicker}
        >
          <Element3 size={14} variant="Linear" color={theme.palette.primary.main} />
          {showIconPicker
            ? t("products:create.iconPickerClose")
            : t("products:create.iconPickerOpen")}
        </Box>
      </Box>
    </Box>
  );

  const renderMobileImageRow = () => (
    <>
      <Box sx={styles.imageRow}>
        {imagePreview ? (
          <Box
            component="img"
            src={imagePreview}
            alt=""
            sx={styles.imagePreview(MOBILE_IMAGE_SIZE, 22)}
            onClick={handleImageSelect}
          />
        ) : (
          <Box
            sx={styles.imageSlot(MOBILE_IMAGE_SIZE, 22)}
            onClick={handleImageSelect}
          >
            <Typography sx={styles.imagePlaceholderText}>
              {t("products:create.fields.image")}
            </Typography>
          </Box>
        )}
        <Box sx={styles.imageMeta}>
          <Typography sx={styles.imageTitle}>{t("products:create.fields.image")}</Typography>
          <Box
            component="button"
            type="button"
            sx={styles.ghostLink}
            onClick={toggleIconPicker}
          >
            <Element3 size={13} variant="Linear" color={theme.palette.primary.main} />
            {showIconPicker
              ? t("products:create.iconPickerClose")
              : t("products:create.iconPickerOpen")}
          </Box>
        </Box>
      </Box>
      {showIconPicker ? (
        <Box sx={styles.iconPickerRow}>
          {PRODUCT_ICON_OPTIONS.map((option) => {
            const selected = option.id === iconId;
            return (
              <Box
                key={option.id}
                sx={styles.iconTilePick(selected, MOBILE_ICON_TILE_SIZE)}
                onClick={() => handleSelectIcon(option.id)}
                title={option.label}
              >
                {renderProductIcon(
                  option.id,
                  18,
                  selected ? theme.palette.primary.main : theme.palette.text.secondary,
                )}
              </Box>
            );
          })}
        </Box>
      ) : null}
    </>
  );

  const renderIconPicker = () =>
    showIconPicker ? (
      <Box sx={styles.iconPickerRow}>
        {PRODUCT_ICON_OPTIONS.map((option) => {
          const selected = option.id === iconId;
          return (
            <Box
              key={option.id}
              sx={styles.iconTilePick(selected, DESKTOP_ICON_TILE_SIZE)}
              onClick={() => handleSelectIcon(option.id)}
              title={option.label}
            >
              {renderProductIcon(
                option.id,
                21,
                selected ? theme.palette.primary.main : theme.palette.text.secondary,
              )}
            </Box>
          );
        })}
      </Box>
    ) : null;

  const renderIdentitySection = (isMobile: boolean) => (
    <Box sx={styles.stepSection}>
      <Box sx={styles.stepHeader}>
        <Box sx={styles.stepNumber}>۱</Box>
        <Typography sx={styles.stepTitle}>{t("products:create.steps.identity")}</Typography>
      </Box>
      <FloatingTextField
        label={t("products:create.fields.name")}
        placeholder={t("products:create.placeholders.name")}
        error={Boolean(errors.name)}
        helperText={errors.name?.message}
        {...register("name")}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Controller
          name="category_id"
          control={control}
          render={({ field }) => (
            <SelectField
              label={t("products:create.fields.category")}
              value={field.value !== undefined ? String(field.value) : ""}
              onChange={(event) => handleCategoryChange(event.target.value as string)}
              options={categoryOptions}
            />
          )}
        />
        <Box
          component="button"
          type="button"
          sx={styles.ghostLink}
          onClick={handleOpenCategoryModal}
        >
          <AddCircle
            size={isMobile ? 13 : 14}
            variant="Linear"
            color={theme.palette.primary.main}
          />
          {t("products:create.category.addNew")}
        </Box>
      </Box>
      {showSku ? (
        <Box sx={styles.reveal}>
          <FloatingTextField
            label={t("products:create.fields.sku")}
            placeholder={t("products:create.placeholders.sku")}
            sx={styles.skuField}
            error={Boolean(errors.sku)}
            helperText={errors.sku?.message}
            {...register("sku")}
          />
        </Box>
      ) : null}
      {showDescription ? (
        <Box sx={styles.reveal}>
          <FloatingTextField
            label={t("products:create.fields.description")}
            placeholder={
              isMobile
                ? t("products:create.placeholders.descriptionMobile")
                : t("products:create.placeholders.description")
            }
            multiline
            rows={3}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
            {...register("description")}
          />
        </Box>
      ) : null}
      <Box sx={styles.optionalLinksRow}>
        {!showSku ? (
          <Box component="button" type="button" sx={styles.ghostLink} onClick={handleShowSku}>
            <AddCircle
              size={isMobile ? 13 : 14}
              variant="Linear"
              color={theme.palette.primary.main}
            />
            {isMobile ? t("products:create.addSkuShort") : t("products:create.addSku")}
          </Box>
        ) : null}
        {!showDescription ? (
          <Box
            component="button"
            type="button"
            sx={styles.ghostLink}
            onClick={handleShowDescription}
          >
            <AddCircle
              size={isMobile ? 13 : 14}
              variant="Linear"
              color={theme.palette.primary.main}
            />
            {isMobile ? t("products:create.addDescriptionShort") : t("products:create.addDescription")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  const renderPricingSection = () => (
    <Box sx={styles.stepSection}>
      <Box sx={styles.stepHeader}>
        <Box sx={styles.stepNumber}>۲</Box>
        <Typography sx={styles.stepTitle}>{t("products:create.steps.pricing")}</Typography>
      </Box>
      <Controller
        name="price"
        control={control}
        render={({ field }) => (
          <FloatingTextField
            label={t("products:create.fields.price")}
            placeholder={t("products:create.placeholders.price")}
            type="text"
            inputMode="numeric"
            fieldState={priceFieldState}
            error={priceFieldState === "error"}
            helperText={priceHelper}
            value={field.value > 0 ? String(field.value) : ""}
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, "");
              field.onChange(digits === "" ? 0 : Number(digits));
            }}
            onBlur={field.onBlur}
            name={field.name}
            inputRef={field.ref}
          />
        )}
      />
      <Box sx={styles.stockRow}>
        <Box>
          <Typography sx={styles.stockLabel}>{t("products:create.fields.stock")}</Typography>
          <Typography sx={styles.stockHint}>{t("products:create.stockHint")}</Typography>
        </Box>
        <Controller
          name="stock"
          control={control}
          render={({ field }) => (
            <QuantityStepper
              value={field.value}
              min={0}
              max={9999}
              onChange={field.onChange}
            />
          )}
        />
      </Box>
    </Box>
  );

  const renderVisibilitySection = () => (
    <Box sx={styles.stepSection}>
      <Box sx={styles.stepHeader}>
        <Box sx={styles.stepNumber}>۳</Box>
        <Typography sx={styles.stepTitle}>{t("products:create.steps.visibility")}</Typography>
      </Box>
      <Controller
        name="is_active"
        control={control}
        render={({ field }) => (
          <>
            <Box sx={styles.desktopCheckboxLabel}>
              <SefanoCheckbox
                label={t("products:create.fields.isActive")}
                checked={field.value}
                onChange={field.onChange}
              />
            </Box>
            <Box sx={styles.mobileCheckboxLabel}>
              <SefanoCheckbox
                label={t("products:create.fields.isActiveMobile")}
                checked={field.value}
                onChange={field.onChange}
              />
            </Box>
          </>
        )}
      />
    </Box>
  );

  const desktopPreviewPanel = (
    <>
      <Box sx={styles.previewCard}>
        <Box sx={styles.previewAccent} />
        <Box sx={styles.previewBody}>
          <Box sx={styles.previewHeader}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700 }}>
              {t("products:create.preview.title")}
            </Typography>
            <StatusBadge color={previewStatus.color} size="sm" label={previewStatus.label} />
          </Box>
          <Box sx={styles.previewProductRow}>
            <ProductIconTile icon={previewIcon} size={52} />
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography sx={styles.previewName}>{previewName}</Typography>
              <Typography sx={styles.categoryPill}>{selectedCategoryName}</Typography>
            </Box>
          </Box>
          <Divider />
          <Box sx={styles.previewRow}>
            <Typography sx={styles.previewLabel}>{t("products:create.preview.price")}</Typography>
            <Typography sx={styles.previewValue}>
              {previewPrice}{" "}
              <Typography
                component="span"
                sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "text.secondary" }}
              >
                {t("products:create.preview.currency")}
              </Typography>
            </Typography>
          </Box>
          <Box sx={styles.previewRow}>
            <Typography sx={styles.previewLabel}>{t("products:create.preview.stock")}</Typography>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700 }}>
              {formatPersianNumber(watchedStock)} {t("products:create.preview.unit")}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <DocumentText size={16} variant="Bold" color={theme.palette.text.secondary} />
            <Typography
              sx={{
                fontSize: "0.78125rem",
                lineHeight: 1.7,
                color: watchedDescription?.trim() ? "text.primary" : "text.secondary",
              }}
            >
              {watchedDescription?.trim() || t("products:create.preview.noDescription")}
            </Typography>
          </Box>
        </Box>
      </Box>

      {!isEditMode ? (
        <Box sx={styles.benefitsCard}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
            {t("products:create.benefits.title")}
          </Typography>
          {[
            { icon: Link1, key: "link" },
            { icon: Setting2, key: "catalog" },
            { icon: PenAdd, key: "edit" },
          ].map(({ icon: Icon, key }) => (
            <Box key={key} sx={styles.benefitRow}>
              <Icon size={19} variant="Bold" color={theme.palette.secondary.main} />
              <Typography sx={styles.benefitText}>{t(`products:create.benefits.${key}`)}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );

  const mobileCompactPreview = (
    <Box sx={styles.mobileCompactPreview}>
      <Box sx={styles.mobilePreviewRow}>
        <ProductIconTile icon={previewIcon} size={40} />
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography sx={styles.mobilePreviewName}>{previewName}</Typography>
          <Typography sx={styles.mobilePreviewCategory}>{selectedCategoryName}</Typography>
        </Box>
        <StatusBadge color={previewStatus.color} size="sm" label={previewStatus.label} />
      </Box>
      <Divider />
      <Box sx={styles.previewRow}>
        <Typography sx={styles.previewLabel}>{t("products:create.preview.price")}</Typography>
        <Typography sx={styles.mobilePreviewPrice}>
          {previewPrice} {t("products:create.preview.currency")}
        </Typography>
      </Box>
    </Box>
  );

  const categoryModal = (
    <Dialog
      open={categoryModalOpen}
      onClose={handleCloseCategoryModal}
      slotProps={{
        backdrop: { sx: { bgcolor: alpha(theme.palette.text.primary, 0.35) } },
        paper: { sx: styles.categoryModalPaper },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={styles.categoryModalHeader}>
          <Box sx={styles.categoryModalTitleRow}>
            <Tag2 size={19} variant="Bold" color={theme.palette.primary.main} />
            <Typography sx={styles.categoryModalTitle}>
              {t("products:create.category.modalTitle")}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            sx={styles.categoryModalCloseButton}
            onClick={handleCloseCategoryModal}
            aria-label={t("common:actions.cancel")}
          >
            <CloseCircle size={20} variant="Linear" color={theme.palette.text.secondary} />
          </Box>
        </Box>
        <Typography sx={styles.categoryModalDescription}>
          {t("products:create.category.modalDescription")}
        </Typography>
        <FloatingTextField
          label={t("products:create.category.nameLabel")}
          placeholder={t("products:create.category.namePlaceholder")}
          value={newCategoryName}
          onChange={(event) => setNewCategoryName(event.target.value)}
          fieldState={isDuplicateCategory ? "error" : "default"}
          error={isDuplicateCategory}
          helperText={newCategoryHelper}
        />
        <Box sx={styles.categoryModalActions}>
          <Button
            variant="text"
            fullWidth
            onClick={handleCloseCategoryModal}
            sx={{ height: 46, fontWeight: 600 }}
          >
            {t("common:actions.cancel")}
          </Button>
          <SefanoButton
            fullWidth
            disabled={cannotSaveCategory || isPending}
            onClick={handleSaveNewCategory}
            sx={{ height: 46 }}
          >
            {t("products:create.category.save")}
          </SefanoButton>
        </Box>
      </Box>
    </Dialog>
  );

  if (isSuccess) {
    return (
      <PageTemplate maxWidth={1120}>
        <Box sx={styles.successWrap}>
          <Box sx={styles.successIconWrap}>
            <TickCircle size={46} variant="Bold" color={theme.palette.success.main} />
          </Box>
          <Typography sx={styles.successTitle}>{successTitle}</Typography>
          <Typography sx={styles.successDescription}>
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
              {successDescriptionDesktop}
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
              {successDescriptionMobile}
            </Box>
          </Typography>
          <Box sx={styles.successActionsDesktop}>
            {!isEditMode ? (
              <SefanoButton intent="secondary" fullWidth onClick={handleCreateAnother}>
                {t("products:create.successAddAnother")}
              </SefanoButton>
            ) : null}
            <Link to="/products" style={{ textDecoration: "none", flex: 1 }}>
              <SefanoButton fullWidth>{t("products:create.successBack")}</SefanoButton>
            </Link>
          </Box>
          <Box sx={styles.successActionsMobile}>
            <Link to="/products" style={{ textDecoration: "none" }}>
              <SefanoButton fullWidth>{t("products:create.successBack")}</SefanoButton>
            </Link>
            {!isEditMode ? (
              <Button variant="text" fullWidth onClick={handleCreateAnother} sx={{ height: 44 }}>
                {t("products:create.successAddAnother")}
              </Button>
            ) : null}
          </Box>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate maxWidth={1120}>
      {categoryModal}

      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        accept="image/*"
        sx={styles.hiddenInput}
        onChange={handleImageChange}
      />

      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopHeaderBlock}>
          <Link to="/products" style={{ textDecoration: "none" }}>
            <Typography component="span" sx={styles.backLink}>
              <ArrowRight2 size={16} variant="Linear" />
              {t("products:create.backToProducts")}
            </Typography>
          </Link>
          <Typography sx={styles.pageTitle}>{pageTitle}</Typography>
          <Typography sx={styles.pageSubtitle}>{pageSubtitle}</Typography>
        </Box>

        <Box sx={styles.mobileHeaderBar}>
          <Link to="/products" style={{ textDecoration: "none" }}>
            <SefanoIconButton variant="plain" sx={styles.mobileIconButton}>
              <ArrowRight2 size={18} variant="Linear" />
            </SefanoIconButton>
          </Link>
          <Typography sx={styles.mobileHeaderTitle}>{pageTitle}</Typography>
        </Box>

        <Box sx={styles.layout}>
          <Box sx={styles.formColumn}>
            <Box
              component="form"
              sx={styles.formCard}
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              {isDesktop ? (
                <Box sx={styles.desktopFormStack}>
                  {renderImageSlot(DESKTOP_IMAGE_SIZE, 26)}
                  {renderIconPicker()}
                  <Divider sx={styles.divider} />
                  {renderIdentitySection(false)}
                  <Divider sx={styles.divider} />
                  {renderPricingSection()}
                  <Divider sx={styles.divider} />
                  {renderVisibilitySection()}
                  <Box sx={styles.formActions}>
                    <SefanoButton
                      type="submit"
                      fullWidth
                      disabled={isPending || !canSubmit}
                      sx={{ height: 50 }}
                    >
                      {isPending ? t("common:actions.loading") : submitLabel}
                    </SefanoButton>
                    <Link to="/products" style={{ textDecoration: "none" }}>
                      <Button variant="text" sx={styles.cancelButton}>
                        {t("common:actions.cancel")}
                      </Button>
                    </Link>
                  </Box>
                  {!canSubmit ? (
                    <Typography sx={styles.submitHint}>{submitHint}</Typography>
                  ) : null}
                </Box>
              ) : (
                <Box sx={styles.mobileFormStack}>
                  {mobileCompactPreview}
                  {renderMobileImageRow()}
                  <Divider sx={styles.divider} />
                  {renderIdentitySection(true)}
                  <Divider sx={styles.divider} />
                  {renderPricingSection()}
                  <Divider sx={styles.divider} />
                  {renderVisibilitySection()}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={styles.previewColumn}>{desktopPreviewPanel}</Box>
        </Box>
      </Box>

      <Box sx={styles.stickyCta}>
        <SefanoButton
          fullWidth
          disabled={isPending || !canSubmit}
          onClick={handleSubmit(onSubmit)}
          sx={{ height: 48 }}
        >
          {isPending ? t("common:actions.loading") : submitLabel}
        </SefanoButton>
        {!canSubmit ? <Typography sx={styles.submitHint}>{submitHint}</Typography> : null}
      </Box>
    </PageTemplate>
  );
}
